import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { SearchFieldWrapperStyles } from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomerPartNumber } from "@thos/Services/ThosApiModels";
import createCustomerPartNumber from "@thos/Store/Data/CustomerPartNumbers/Handlers/CreateCustomerPartNumber";
import updateCustomerPartNumber from "@thos/Store/Data/CustomerPartNumbers/Handlers/UpdateCustomerPartNumber";
import debounce from "lodash/debounce";
import React, { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type FormType = "create" | "update";

interface OwnProps {
    formType: FormType;
    customerPartNumber: CustomerPartNumber;
    onCancel: (e: any) => void;
    onSubmit: (e: any, customerPartNumber: CustomerPartNumber) => void;
}

interface State {
    customerPartNumber: CustomerPartNumber;
    customerPartNumbers: string[];
    selectedProductId?: string;
    options: OptionObject[];
    selectedProduct?: ProductModel;
}

const mapDispatchToProps = {
    getAutocompleteModel,
    loadProduct,
    createCustomerPartNumber,
    updateCustomerPartNumber,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerPartNumberFormStyles {
    wrapper?: SearchFieldWrapperStyles;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    myPartNumbersLabel?: TypographyPresentationProps;
    addProductsContainer?: InjectableCss;
    addProductRow?: InjectableCss;
    addButton?: ButtonPresentationProps;
    removeButton?: ButtonPresentationProps;
    formActionsContainer?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    selectedProductContainer?: InjectableCss;
    productInfo?: InjectableCss;
    selectedProductInfoContainer?: InjectableCss;
    productNumberValue?: TypographyPresentationProps;
    productNumberLabel?: TypographyPresentationProps;
    productDescription?: TypographyPresentationProps;
    errorMessage?: TypographyPresentationProps;
}

export const customerPartNumberFormStyles: CustomerPartNumberFormStyles = {
    optionWrapper: {
        css: css`
            display: flex;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    addProductsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    addProductRow: {
        css: css`
            display: flex;
            gap: 5px;
            margin: 5px 0;
        `,
    },
    myPartNumbersLabel: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            min-height: 22px;
            display: inline-block;
            width: 100%;
            margin: 10px 0 5px;
        `,
    },
    removeButton: {
        variant: "tertiary",
    },
    formActionsContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 5px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    selectedProductContainer: {
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            img {
                max-width: 200px;
            }
        `,
    },
    productInfo: {
        css: css`
            margin-top: 15px;
        `,
    },
    selectedProductInfoContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-left: 10px;
            // width: 50%;
        `,
    },
    productNumberValue: {
        size: 16,
        css: css`
            margin-bottom: 5px;
        `,
    },
    productNumberLabel: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 5px;
        `,
    },
    productDescription: {
        size: 16,
        weight: "bold",
    },
    addButton: {
        buttonType: "outline",
    },
    errorMessage: {
        color: "danger",
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
};
const styles = customerPartNumberFormStyles;

class CustomerPartNumberForm extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            customerPartNumber: props.customerPartNumber,
            selectedProductId: props.customerPartNumber?.product.id,
            customerPartNumbers: props.customerPartNumber?.customerPartNumbers ?? [],
            options: [],
            selectedProduct: props.customerPartNumber?.product,
        };
        this.productSearchChangeHandler = this.productSearchChangeHandler.bind(this);
        this.productSelectionChangeHandler = this.productSelectionChangeHandler.bind(this);
        this.addCustomerPartNumber = this.addCustomerPartNumber.bind(this);
        this.customerPartNumberChangeHandler = this.customerPartNumberChangeHandler.bind(this);
        this.removeCustomerPartNumber = this.removeCustomerPartNumber.bind(this);
        this.submit = this.submit.bind(this);
        this.cancel = this.cancel.bind(this);
        this.debouncedSearchProducts = debounce(this.debouncedSearchProducts, 300);
    }

    productSelectionChangeHandler(value?: string) {
        if (value) {
            this.props.loadProduct({
                id: value,
                onSuccess: result => {
                    this.setState({
                        ...this.state,
                        customerPartNumber: {
                            ...this.state.customerPartNumber,
                            product: result,
                            validationMessage: undefined,
                        },
                        selectedProductId: result.id,
                        selectedProduct: result,
                    });
                },
            });
        } else {
            this.setState({ ...this.state, selectedProductId: "", selectedProduct: undefined });
        }
    }

    debouncedSearchProducts(query: string) {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (!result?.products) {
                    return;
                }

                const newOptions = result.products.map(product => ({
                    optionText: product.erpNumber,
                    optionValue: product.id || undefined,
                    rowChildren: (
                        <StyledWrapper {...styles.optionWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage {...styles.productImage} src={product.image} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ),
                }));
                this.setState({ ...this.state, options: newOptions });
            },
        });
    }

    productSearchChangeHandler(event: ChangeEvent<HTMLInputElement>) {
        this.debouncedSearchProducts(event.target.value);
    }

    addCustomerPartNumber(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        const newPartNumbers = this.state.customerPartNumbers.concat("") ?? [];
        this.setState({ ...this.state, customerPartNumbers: newPartNumbers });
    }

    removeCustomerPartNumber(event: React.MouseEvent<HTMLButtonElement>) {
        const indexToRemove = parseInt(event.currentTarget.dataset["index"] ?? "", 10);
        if ((!indexToRemove && indexToRemove !== 0) || this.state.customerPartNumbers.length < indexToRemove + 1) {
            return;
        }
        const newPartNumbers = this.state.customerPartNumbers.filter((item, index) => {
            return index !== indexToRemove;
        });
        this.setState({ ...this.state, customerPartNumbers: newPartNumbers });
    }

    customerPartNumberChangeHandler(event: ChangeEvent<HTMLInputElement>) {
        const index = parseInt(event.target.dataset["index"] ?? "", 10);
        if ((!index && index !== 0) || this.state.customerPartNumbers.length < index + 1) {
            return;
        }

        const newPartNumbers = this.state.customerPartNumbers.slice();
        newPartNumbers[index] = event.target.value;
        this.setState({
            ...this.state,
            customerPartNumber: { ...this.state.customerPartNumber, validationMessage: undefined },
            customerPartNumbers: newPartNumbers,
        });
    }

    submit(e: any) {
        if (this.state.customerPartNumbers.length < 1) {
            this.setState({
                ...this.state,
                customerPartNumber: {
                    ...this.state.customerPartNumber,
                    validationMessage: translate("Please add at least one part number"),
                },
            });
            return;
        }

        if (
            this.state.customerPartNumber &&
            this.state.customerPartNumber.validationMessage !== undefined &&
            this.state.customerPartNumber.validationMessage !== null &&
            this.state.customerPartNumber!.validationMessage!.length > 0
        ) {
            this.setState({
                ...this.state,
                customerPartNumber: {
                    ...this.state.customerPartNumber,
                    validationMessage: undefined,
                },
            });
        }

        const newCustomerPartNumber = {
            ...this.state.customerPartNumber,
            id: this.state.customerPartNumber.id ?? "",
            customerPartNumbers: this.state.customerPartNumbers,
        };
        if (this.props.formType === "create") {
            if (!this.state.selectedProductId) {
                this.setState({
                    ...this.state,
                    customerPartNumber: {
                        ...this.state.customerPartNumber,
                        validationMessage: translate("Please select a product"),
                    },
                });
                return;
            }

            this.props.createCustomerPartNumber({
                partId: newCustomerPartNumber.product.id,
                customerPartNumbers: newCustomerPartNumber.customerPartNumbers,
                onSuccess: (customerPartNumber: CustomerPartNumber) => {
                    this.props.onSubmit(e, customerPartNumber);
                },
                onError: (customerPartNumberError: CustomerPartNumber) => {
                    this.setState({
                        ...this.state,
                        customerPartNumber: {
                            ...this.state.customerPartNumber,
                            validationMessage: customerPartNumberError.validationMessage,
                        },
                    });
                },
            });
        } else {
            this.props.updateCustomerPartNumber({
                customerPartNumber: newCustomerPartNumber,
                onSuccess: (customerPartNumber: CustomerPartNumber) => {
                    this.props.onSubmit(e, customerPartNumber);
                },
                onError: (customerPartNumberError: CustomerPartNumber) => {
                    this.setState({
                        ...this.state,
                        customerPartNumber: {
                            ...this.state.customerPartNumber,
                            validationMessage: customerPartNumberError.validationMessage,
                        },
                    });
                },
            });
        }
    }

    cancel(e: any) {
        this.props.onCancel(e);
    }

    render() {
        const { selectedProductId, selectedProduct, customerPartNumber, options } = this.state;
        const brandDisplay = selectedProduct?.brand ? (
            selectedProduct?.brand.logoSmallImagePath ? (
                <LazyImage
                    src={selectedProduct?.brand.logoSmallImagePath}
                    altText={selectedProduct?.brand.logoImageAltText || selectedProduct?.brand.name}
                />
            ) : (
                <Typography>{selectedProduct.brand.name}</Typography>
            )
        ) : (
            ""
        );

        return (
            <>
                {customerPartNumber.validationMessage && (
                    <Typography {...styles.errorMessage}>{customerPartNumber.validationMessage}</Typography>
                )}
                {!customerPartNumber.id && (
                    <DynamicDropdown
                        label={translate("Product")}
                        onSelectionChange={this.productSelectionChangeHandler}
                        onInputChange={this.productSearchChangeHandler}
                        filterOption={() => true}
                        selected={selectedProductId}
                        options={options}
                        placeholder={translate("Enter keyword or item #")}
                    />
                )}
                {selectedProduct?.id && (
                    <StyledWrapper>
                        <StyledWrapper {...styles.selectedProductContainer}>
                            <LazyImage src={selectedProduct?.smallImagePath} altText={selectedProduct?.imageAltText} />
                            <StyledWrapper {...styles.selectedProductInfoContainer}>{brandDisplay}</StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.productInfo}>
                            <Typography {...styles.productDescription}>{selectedProduct.productTitle}</Typography>
                            <Typography {...styles.productNumberValue} as="p">
                                <Typography {...styles.productNumberLabel}>{translate("Part #")}</Typography>
                                {selectedProduct.productNumber}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <Typography as="label" {...styles.myPartNumbersLabel}>
                    {translate("My Part Numbers")}
                </Typography>
                <StyledWrapper {...styles.addProductsContainer}>
                    {this.state.customerPartNumbers.map((item: string, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper key={`${index}`} {...styles.addProductRow}>
                            <TextField
                                data-index={index}
                                value={item}
                                onChange={this.customerPartNumberChangeHandler}
                            />
                            <Button {...styles.removeButton} data-index={index} onClick={this.removeCustomerPartNumber}>
                                {translate("Remove")}
                            </Button>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
                <StyledWrapper>
                    <Button {...styles.addButton} onClick={this.addCustomerPartNumber}>
                        {translate("Add")}
                    </Button>
                </StyledWrapper>
                <StyledWrapper {...styles.formActionsContainer}>
                    <Button onClick={this.submit}>{translate("Submit")}</Button>
                    <Button {...styles.cancelButton} onClick={this.cancel}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </>
        );
    }
}

export default connect(null, mapDispatchToProps)(CustomerPartNumberForm);
